import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShieldIcon, ShuffleIcon, TimerIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// HOCKEY READY™ copy. Every claim mirrors the real deliverable (6 volumes + 5 bonuses).

// ── IMAGES ──
const IMG_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/1c63e04f-08e3-4b74-8ade-ba313a1d3da5/';
// Hero + Offer mockup: box + 6 volumes (transparent, 16:9).
export const PRODUCT_IMAGE: string = IMG_BASE + '1792515712821_hockey-mockup-principal-b498ca0b.webp';
// Sample pages carousel ("Look Inside").
export const PAGE_IMAGE_1: string = IMG_BASE + '1792515737870_hockey-pag-calendar-46f382e7.webp';
export const PAGE_IMAGE_2: string = IMG_BASE + '1792515742110_hockey-pag-session-55143c00.webp';
export const PAGE_IMAGE_3: string = IMG_BASE + '1792515746122_hockey-pag-grids-2e532858.webp';
export const PAGE_IMAGE_4: string = IMG_BASE + '1792515750431_hockey-pag-rules-aaf67f0e.webp';
export const PAGE_IMAGE_5: string = IMG_BASE + '1792515754715_hockey-pag-emergency-f5db4a00.webp';
// Real Volume 1 cover (shown in the Volume 01 panel of "What's Inside").
export const VOLUME_1_COVER: string = IMG_BASE + '1792515758794_hockey-vol1-cover-49971263.webp';

export const BRAND = 'HOCKEY READY™';

export const heroGuarantees = ['Instant digital access', 'One-time payment', 'No subscription', '7-day guarantee'];

export const stats = [{
  value: '240',
  label: 'Pages across the six volumes'
}, {
  value: '6',
  label: 'Coaching volumes'
}, {
  value: '5',
  label: 'Bonuses included'
}, {
  value: '40',
  label: 'Weeks of season, already planned'
}];

export const differenceStats = [{
  value: 'One full season',
  label: 'From tryouts and preseason to the championship run'
}, {
  value: '6 coaching volumes',
  label: 'Foundation, technique, defense, mid-season, elite performance, and peaking'
}, {
  value: 'Printable PDFs',
  label: 'Use them on your phone at the field or print the sessions you need'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Your Sunday nights disappear',
  text: 'Hours at the kitchen table building next week’s practices — and you’re still not done.'
}, {
  title: 'Hours spent searching for drills',
  text: 'Videos and drills from different sites are too basic, too advanced, or written for players, not coaches.'
}, {
  title: 'Every practice starts from scratch',
  text: 'Warm-up, drills, timing, coaching points — what should take minutes becomes another long task.'
}, {
  title: 'No real plan for the whole season',
  text: 'You know what to do this week, but not how it all builds toward the games that matter.'
}, {
  title: 'Game day adds more work',
  text: 'Lineups, subs, halftime talks, and parent questions — on top of planning every practice.'
}, {
  title: 'Stress before every practice',
  text: 'When time runs out, you end up throwing a practice together in the parking lot.'
}];

export const afterItems = ['Your whole field hockey season planned week by week', 'Practices timed minute by minute, ready to run', 'Season calendars for fall high school, club, and middle school', 'Stick skills, receiving, passing, and 1v1 progressions', 'Defensive sessions, including defending penalty corners', 'Game management, scouting, and halftime adjustments', 'Speed, agility, and mental skills work', 'A peaking and taper plan for the championship run', 'Printable PDFs you can take to the field'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Your Sunday nights are spent planning',
  text: 'Do you spend part of your weekend building practices for the week ahead?'
}, {
  icon: FolderSearchIcon,
  title: 'You don’t know which drill to run',
  text: 'Are you constantly searching for drills that fit your players’ age and level?'
}, {
  icon: LightbulbIcon,
  title: 'You want clearer coaching points',
  text: 'Do you need simple ways to teach receiving, passing, defending, and decision-making?'
}, {
  icon: PencilRulerIcon,
  title: 'You want to stop building everything from zero',
  text: 'Do you wish your practices were already planned, timed, and ready to run?'
}, {
  icon: ShuffleIcon,
  title: 'Your players need more variety',
  text: 'Would you like fresh warm-ups, games, and small-sided activities that keep practice moving?'
}, {
  icon: LayoutGridIcon,
  title: 'You need a plan for the whole season',
  text: 'Would you like every week mapped out so you always know what comes next?'
}];

// Spine colours mirror the six volume covers.
export const volumes: Array<{
  number: string;
  title: string;
  description: string;
  heading?: string;
  intro?: string;
  items?: string[];
  outro?: string;
  cover?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  title: 'The Foundation',
  description: 'Weeks 1–6: plan your season, run your first practices, and set up tryouts and safety.',
  heading: 'Start Your Season Organized from Day One.',
  intro: 'Weeks 1–6. The first volume sets up your whole season and gives you your first practices, ready to run.',
  items: ['Season planner with 3 calendar templates', 'Fall high school, club year-round, and middle school/rec calendars', 'The anatomy of a 90-minute practice', '18 practices ready to run, minute by minute', 'Tryouts plan', 'Safety checklist', 'Emergency action plans'],
  outro: 'Open it, pick the practice, and walk onto the field with a plan.',
  cover: VOLUME_1_COVER,
  spine: 'bg-red-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  title: 'Technical Mastery',
  description: 'Weeks 7–12: 18 ready-to-run practices on the 6 technical pillars (grip & carry, receiving, passing, eliminating defenders, tackling, scanning), a 30-drill library, 15 mistakes & fixes, 40 coaching cues, and an 8-test skills assessment.',
  heading: 'Build the Skills Every Player Needs.',
  intro: 'Weeks 7–12. 18 ready-to-run practices on the 6 technical pillars — grip & carry, receiving, passing, eliminating defenders, tackling, and scanning — plus a 30-drill library, 15 mistakes & fixes, 40 coaching cues, and an 8-test skills assessment.',
  items: ['18 practices (weeks 7–12)', 'Pull-left, V-drag, spin & 1v1 to goal', 'Jab, block tackle & channeling', '30 extra drills + mistakes & fixes', 'Technical testing & progress card'],
  spine: 'bg-amber-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  title: 'Defensive Wall',
  description: 'Weeks 13–18: 24 sessions to build a compact, organized defense.',
  heading: 'Build a Defense That Holds Together.',
  intro: 'Weeks 13–18. Twenty-four sessions that teach your team to defend as a unit.',
  items: ['24 defensive sessions', 'Compactness and team shape', 'Pressing triggers', 'Defending penalty corners'],
  spine: 'bg-green-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  title: 'Mid-Season Pivot',
  description: 'Weeks 19–24: manage games, scout opponents, and adjust at halftime.',
  heading: 'Coach the Game, Not Just the Practice.',
  intro: 'Weeks 19–24. The tools you need once the games start piling up.',
  items: ['Game management', 'Opponent scouting', 'Halftime adjustments'],
  spine: 'bg-blue-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 05',
  title: 'Elite Performance',
  description: 'Weeks 25–30: speed, agility, mental skills, and advanced technique.',
  heading: 'Take Your Players to the Next Level.',
  intro: 'Weeks 25–30. Physical, mental, and technical work for players ready for more.',
  items: ['Speed and agility', 'Mental skills', 'First touch', 'Reverse stick', 'Drag flick basics'],
  spine: 'bg-purple-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 06',
  title: 'The Championship Run',
  description: 'Weeks 31–40: peak at the right time with set pieces, taper, and a game-day timeline.',
  heading: 'Peak When It Matters Most.',
  intro: 'Weeks 31–40. The plan for the end of your season, when every game counts.',
  items: ['Peaking plan', 'Set pieces', 'Taper', 'Game-day timeline'],
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 Coaching Volumes for Your Whole Field Hockey Season',
  text: 'A 40-week season plan you can compress for a 12–14 week fall high school season or stretch for club. Each volume has about 40 pages of practices, drills, and coaching tools — around 240 pages in total.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ready-to-run practices',
  text: 'Stop starting with a blank page. Practices are planned minute by minute so you know exactly what to run.'
}, {
  icon: CalendarCheckIcon,
  title: 'A whole season, planned',
  text: 'A 40-week plan with calendar templates for fall high school, club year-round, and middle school/rec.'
}, {
  icon: TimerIcon,
  title: 'Warm-ups and agility',
  text: '25 activations ready to start every practice, plus speed and agility sessions.'
}, {
  icon: ShieldIcon,
  title: 'Defense and set pieces',
  text: 'Compactness, pressing triggers, and penalty corners — both defending and attacking them.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Testing and report cards',
  text: 'Fitness and skill tests plus player report cards to track progress through the season.'
}, {
  icon: FileTextIcon,
  title: 'Game-day tools',
  text: 'A T-90 routine, bench communication, subs, and what to say before, during, and after the game.'
}];

export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: PAGE_IMAGE_1,
  label: 'Season Planner',
  title: 'Map the 6 Volumes to YOUR Calendar',
  text: 'Three ready-made calendars: fall high school (13 weeks), club year-round (40 weeks), and middle school (9 weeks). Pick yours and the season is mapped.'
}, {
  src: PAGE_IMAGE_2,
  label: 'Ready-to-Run Practice',
  title: 'Week 1 · Session 1 — 90 Minutes, Minute by Minute',
  text: 'Activate, skill block, game-like play, and scrimmage — with coaching cues and ways to scale up or down.'
}, {
  src: PAGE_IMAGE_3,
  label: 'Practice Setup',
  title: 'The 6 Standard Cone Grids',
  text: 'Every drill uses one of 6 grids measured in yards — set up your field in 2 minutes.'
}, {
  src: PAGE_IMAGE_4,
  label: 'Rules in Action',
  title: '10 Rules New Players Get Wrong',
  text: 'Obstruction, back stick, dangerous ball, the 5-yard rule… plus a referee game to make the rules stick.'
}, {
  src: PAGE_IMAGE_5,
  label: 'Emergency Plans',
  title: 'Rain, 8 Players, Half Field, 30 Minutes',
  text: 'A ready session for every bad-day scenario, so practice still happens.'
}];

export const pageTypes = ['Season calendars', 'Ready-to-run practices', 'Drills and sessions', 'Game-day tools'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'The Coach’s Scriptbook',
  text: 'What to say to parents and players — so you never have to find the words on the spot.',
  items: ['Talking to parents', 'Talking to players', 'Pre-game and halftime talks', 'Post-game talks'],
  benefit: 'The right words, ready when you need them.'
}, {
  title: 'Warm-Up & Agility Library',
  text: '25 activations to start every practice the right way.',
  items: ['25 ready-to-use activations', 'Warm-ups for practice and games', 'Agility work', 'Easy to drop into any session'],
  benefit: 'Never wonder how to start practice again.'
}, {
  title: 'The Goalscoring Blueprint',
  text: 'A clear system for creating chances and putting them away.',
  items: ['Circle entries', 'Finishing', 'Penalty corner attack system', 'Attacking sessions'],
  benefit: 'Turn possession into goals.'
}, {
  title: 'Game-Day Management Pro',
  text: 'Everything you need to run game day calmly and in control.',
  items: ['T-90 pre-game routine', 'Bench communication', 'Substitutions', 'Game-day organization'],
  benefit: 'Game day under control, from warm-up to final whistle.'
}, {
  title: 'Performance Testing Tracker',
  text: 'Measure your players’ progress through the season.',
  items: ['Fitness tests', 'Skill tests', 'Player report cards', 'Season progress tracking'],
  benefit: 'See your players’ progress on paper.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Open HOCKEY READY™',
  text: 'Get instant access and open the volume for the week of the season you’re in.'
}, {
  title: 'Choose Your Practice',
  text: 'Pick the week’s practice and the drills that fit your players’ age and level.'
}, {
  title: 'Prepare in 5 Minutes',
  text: 'Review the timing and coaching points, and scale the session up or down.'
}, {
  title: 'Print and Coach',
  text: 'Print the practice or open it on your phone — and walk onto the field with a clear plan.'
}];

export const audienceItems = ['Coach field hockey at youth, middle school, high school, or club level', 'Want your whole season planned and organized', 'Want to stop building every practice from scratch', 'Need ready-to-run drills and sessions', 'Want a clear plan for defense, game day, and the postseason', 'Need simple tools to test and track your players', 'Want to save time on weekly planning', 'Want to feel prepared at every practice and every game'];

export const timeValueTasks = ['Searching for drills', 'Planning and timing each practice', 'Building a season calendar', 'Preparing game-day plans and talks', 'Creating tests and player feedback'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 coaching volumes — about 240 pages',
  detail: 'Foundation, technical mastery, defense, mid-season, elite performance, and the championship run'
}, {
  label: '5 bonuses at no extra cost',
  detail: 'Coach’s scriptbook, warm-up library, goalscoring blueprint, game-day management, and testing tracker'
}, {
  label: 'A 40-week season plan',
  detail: 'Compress it for a 12–14 week fall high school season or stretch it for club'
}];

export const offerIncludes = ['Instant digital access', 'Printable PDFs', 'Delivered via Google Drive', 'No physical product shipped'];

export const PRICE_AMOUNT = '$19';
export const PRICE_CURRENCY = 'USD';
export const PRICE_COMPARE = '$97';
export const PRICE = `${PRICE_AMOUNT} ${PRICE_CURRENCY}`;

export const faqs = [{
  q: 'How do I receive the material?',
  a: 'HOCKEY READY™ is delivered digitally. Right after purchase you get instant access to the PDF files through Google Drive. Download them, open them on your phone, tablet, or computer, and print what you need.'
}, {
  q: 'Can I print it?',
  a: 'Yes. Every volume and bonus is a printable PDF. Print a practice to take to the field, or use it on your phone.'
}, {
  q: 'Does it work for my age group?',
  a: 'Yes. It works from U10 to varsity. Sessions include ways to scale up or down to match your players’ age and level.'
}, {
  q: 'Is it for field hockey or ice hockey?',
  a: 'Field hockey. Every practice, drill, and tool is written for field hockey coaches.'
}, {
  q: 'My high school season is only 12–14 weeks. Does it still work?',
  a: 'Yes. The 40-week plan compresses for a fall high school season and stretches for club. The season planner includes calendar templates for fall high school, club year-round, and middle school/rec.'
}, {
  q: 'Can I really prepare a practice in 5 minutes?',
  a: 'Practices are already planned minute by minute, so you choose the session, review the coaching points, and go. Setting up cones and equipment at the field may take a few extra minutes.'
}, {
  q: 'Is it in English?',
  a: 'Yes. Everything is written in American English, using the terms US and Canadian coaches use.'
}, {
  q: 'Are the five bonuses included?',
  a: `Yes. All five bonuses are included in the ${PRICE} bundle at no extra cost.`
}, {
  q: 'Is this a subscription?',
  a: `No. It is a one-time payment of ${PRICE}. No subscription, no monthly fees.`
}, {
  q: 'Is there a guarantee?',
  a: 'Yes. You have a 7-day guarantee. If it isn’t right for you, email us within 7 days of purchase to request a refund.'
}];
